/**
 * @lines98/client/lib/client-prefs — typed, versioned client-side preferences.
 *
 * ОДИН переиспользуемый модуль для клиентских настроек игрока (НЕ разрозненные
 * `localStorage.getItem` по экранам — owner-directive «не в обход правил»). Любая
 * новая клиентская преференция добавляется ОДНИМ полем в `ClientPrefs` + дефолтом
 * в `DEFAULT_PREFS` + type-guard'ом — без переписывания мест чтения/записи.
 *
 * SCOPE (brief-39 §B/§C + §5.5 settings-панель):
 *   - `liveMoveAnimation` — анимировать ход в ЖИВОЙ игре (BFS-слайд через
 *     `lib/board-animations.animateMoveSlide`). **Default = OFF** (owner-decision
 *     2026-06-02): без явного включения ход применяется мгновенно (как в проде).
 *     Флаг затрагивает ТОЛЬКО live-game; реплей-анимация от него НЕ зависит.
 *   - `theme` — тема приложения (`'retro'` светлая / `'dark'` тёмная), §5.5.
 *     Применяется на `documentElement.dataset.theme` (см. `lib/theme`). Default
 *     `'retro'` (совпадает с `index.html data-theme`). НЕЗАВИСИМА от скина доски
 *     (CLAUDE.md: скин доски — продаваемый продукт, не зависит от light/dark).
 *   - `boardSkin` — id выбранной ПАЛИТРЫ доски (ось «Доска», appearance-skins.md
 *     §4.2; реестр `lib/skin-registry.BOARD_PALETTES`). Default `'standard'`.
 *     Реально применяется к доске+реплеям через `lib/board-appearance` (раньше был
 *     scaffold; со спекой appearance-skins оформление применяется по-настоящему).
 *   - `ballStyle` — id выбранного МАТЕРИАЛА шара (ось «Шары», appearance-skins.md
 *     §4.2; реестр `lib/skin-registry.BALL_STYLES`). Default `'classic'`. Ортогонален
 *     палитре (I-App-2): палитра задаёт цвета, материал — рецепт блика.
 *   - `showRewardScreen` — показывать блок добычи меты на экране итогов (Срез 1b,
 *     meta-jeweler-surfaces.md §4.1; owner: «экран добычи можно выключить»). Default
 *     `true`. Выкл → награды всё равно начисляются сервером, просто без блока.
 *
 * PERSIST:
 *   - localStorage per-origin, единый ключ + version-поле. Corrupted / mis-versioned
 *     blob → дефолты (best-effort, без throw — как `services/replay-store.ts`).
 *   - Запись best-effort (quota / private mode → silent no-op).
 *
 * REACTIVITY:
 *   - `subscribePrefs(listener)` — лёгкий pub/sub (без зависимости от store): экраны,
 *     которым важна live-реакция на смену настройки в той же сессии, подписываются.
 *     `setPref` оповещает подписчиков + кэширует значение в памяти, чтобы повторные
 *     `getClientPrefs()` не парсили localStorage каждый раз.
 *
 * Версионирование: при изменении формы (новое несовместимое поле / переименование)
 * поднять `PREFS_VERSION`; старый blob с иной версией читается как дефолты.
 */

const PREFS_KEY = 'lines98.client-prefs';
const PREFS_VERSION = 1;

// =============================================================================
// Shape
// =============================================================================

/**
 * Клиентские преференции игрока. Каждое поле — независимая настройка с дефолтом
 * в `DEFAULT_PREFS`. Расширяемо: новая настройка = новое поле здесь + дефолт +
 * проверка в `isPartialPrefs` (см. ниже).
 */
/** Тема приложения — значения = `documentElement.dataset.theme` (global.css). */
export type AppTheme = 'retro' | 'dark';

export interface ClientPrefs {
  /**
   * Анимировать ход в живой игре (BFS-слайд). Default `false` — мгновенный ход
   * (brief-39 §B, owner-decision 2026-06-02). Реплей-анимация НЕ зависит от флага.
   */
  readonly liveMoveAnimation: boolean;
  /**
   * Тема приложения (§5.5): `'retro'` светлая / `'dark'` тёмная. Применяется на
   * `documentElement.dataset.theme` через `lib/theme`. Default `'retro'`.
   */
  readonly theme: AppTheme;
  /**
   * Id выбранной ПАЛИТРЫ доски (ось «Доска»; реестр `lib/skin-registry.BOARD_PALETTES`).
   * Default `'standard'`. Применяется к доске+реплеям через `lib/board-appearance`.
   */
  readonly boardSkin: string;
  /**
   * Id выбранного МАТЕРИАЛА шара (ось «Шары»; реестр `lib/skin-registry.BALL_STYLES`).
   * Default `'classic'`. Ортогонален палитре (I-App-2). Forward-compatible: отсутствие
   * поля в старом blob резолвится в дефолт через merge-over-defaults (PREFS_VERSION
   * НЕ поднимаем — аддитивное поле, appearance-skins.md §4.2).
   */
  readonly ballStyle: string;
  /**
   * Показывать блок добычи меты на экране итогов (meta-jeweler-surfaces.md §4.1,
   * owner-decision 2026-06-05 «экран добычи можно в настройках выключить»). Default
   * `true` — блок добычи показывается. Выключено → награды ВСЁ РАВНО начисляются
   * (server-truth, ничего не теряется), просто без анимированного блока (для тех,
   * кому мета не нужна / нужна скорость). НЕ влияет на серверную добычу.
   */
  readonly showRewardScreen: boolean;
}

/** Дефолты — единый источник «из коробки» (используется при пустом / битом blob). */
export const DEFAULT_PREFS: ClientPrefs = {
  liveMoveAnimation: false,
  theme: 'retro',
  boardSkin: 'standard',
  ballStyle: 'classic',
  showRewardScreen: true,
} as const;

interface StoredEnvelope {
  readonly v: number;
  readonly prefs: ClientPrefs;
}

// =============================================================================
// In-memory cache + pub/sub
// =============================================================================

let cache: ClientPrefs | null = null;
const listeners = new Set<(prefs: ClientPrefs) => void>();

/**
 * Текущие преференции. Читает localStorage один раз (затем из памяти-кэша). На
 * отсутствие / битый / mis-versioned blob возвращает `DEFAULT_PREFS`.
 */
export function getClientPrefs(): ClientPrefs {
  if (cache) return cache;
  cache = loadFromStorage();
  return cache;
}

/**
 * Обновить ОДНУ преференцию (partial merge). Пишет в localStorage (best-effort),
 * обновляет кэш и оповещает подписчиков. Возвращает новые полные преференции.
 */
export function setPref<K extends keyof ClientPrefs>(
  key: K,
  value: ClientPrefs[K],
): ClientPrefs {
  const current = getClientPrefs();
  if (current[key] === value) return current; // no-op — без записи/нотификации
  const next: ClientPrefs = { ...current, [key]: value };
  cache = next;
  saveToStorage(next);
  for (const listener of listeners) listener(next);
  return next;
}

/**
 * Подписка на изменение преференций (lightweight pub/sub). Возвращает функцию
 * отписки. Срабатывает на каждый реальный `setPref` (no-op set'ы не оповещают).
 */
export function subscribePrefs(listener: (prefs: ClientPrefs) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Сбросить in-memory кэш (тесты — заставить перечитать localStorage). */
export function _resetPrefsCacheForTest(): void {
  cache = null;
}

// =============================================================================
// Storage I/O (best-effort, defensive)
// =============================================================================

function loadFromStorage(): ClientPrefs {
  try {
    if (typeof window === 'undefined') return DEFAULT_PREFS;
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as unknown;
    if (!isEnvelope(parsed) || parsed.v !== PREFS_VERSION) return DEFAULT_PREFS;
    // Merge over defaults so a future-added field absent in an older blob still
    // resolves to its default (forward-compatible within the same version line).
    return { ...DEFAULT_PREFS, ...parsed.prefs };
  } catch {
    return DEFAULT_PREFS;
  }
}

function saveToStorage(prefs: ClientPrefs): void {
  try {
    if (typeof window === 'undefined') return;
    const envelope: StoredEnvelope = { v: PREFS_VERSION, prefs };
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(envelope));
  } catch {
    // quota / private mode / disabled — non-fatal (matches replay-store).
  }
}

// =============================================================================
// Type guards (defence against tampered / mis-versioned JSON)
// =============================================================================

function isEnvelope(value: unknown): value is StoredEnvelope {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v['v'] === 'number' && isPartialPrefs(v['prefs']);
}

/**
 * Loose validation of the prefs sub-object: every KNOWN field, when present, must
 * have the right type. Unknown fields are ignored (forward-compatible). Add a
 * clause here when adding a field to `ClientPrefs`.
 */
function isPartialPrefs(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if ('liveMoveAnimation' in v && typeof v['liveMoveAnimation'] !== 'boolean') {
    return false;
  }
  if ('theme' in v && v['theme'] !== 'retro' && v['theme'] !== 'dark') {
    return false;
  }
  if ('boardSkin' in v && typeof v['boardSkin'] !== 'string') {
    return false;
  }
  if ('ballStyle' in v && typeof v['ballStyle'] !== 'string') {
    return false;
  }
  if ('showRewardScreen' in v && typeof v['showRewardScreen'] !== 'boolean') {
    return false;
  }
  return true;
}
